import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { routerFactory } from '@/factories'
import { UserRoleEnum } from '@/models/features/user.models'
import { selectUserRole } from '@/reducers/userDataReducer'
import type { TypedTo } from '@/router'
import { isProductionBuild } from '@/utils/environment'

import { useCaptureEvent } from './use-capture-event'
import { useLocalStorage } from './use-local-storage'

export type DesignVersion = 'new' | 'legacy'

export type DesignVersionGroup =
  | 'clients'
  | 'surveys'
  | 'packages'
  | 'social-links'
  | 'home-screen'
  | 'gift-cards'
  | 'maintenance'
  | 'home-screen'
  | 'managers'
  | 'property-managers'
  | 'staff'
  | 'places'
  | 'booking-types'
  | 'services'

// Note: groups listed here are hidden from production except for Administrators (see docs/14-beta-pages.md)
const PRODUCTION_GATED_GROUPS: DesignVersionGroup[] = []

const getDesignVersionGate = (group: DesignVersionGroup, role: string | null) => {
  const isGated = isProductionBuild && PRODUCTION_GATED_GROUPS.includes(group)
  return {
    canToggle: !isGated || role === UserRoleEnum.enum.Administrator,
    defaultVersion: (isGated ? 'legacy' : 'new') as DesignVersion,
    isGated,
  }
}

const getDesignVersionStorageKey = (group: DesignVersionGroup) => `design-version:${group}`

interface UseDesignVersionToggleParams {
  /** Storage group — pages that should share one persisted preference use the same group. */
  group: DesignVersionGroup
  /** Screen name passed to the `design_toggle` analytics event, matching each page's existing value. */
  screen: string
  /** Which design variant this component renders. */
  version: DesignVersion
  /** Route of the counterpart (other) variant. */
  to: TypedTo
}

/**
 * Persists the new/old design preference for a page (or a shared group of pages) in
 * localStorage, and redirects to the counterpart route when the current route doesn't match
 * the stored preference.
 */
export const useDesignVersionToggle = ({
  group,
  screen,
  version,
  to,
}: UseDesignVersionToggleParams) => {
  const role = useSelector(selectUserRole)
  const { canToggle, defaultVersion, isGated } = getDesignVersionGate(group, role)
  const [storedVersion, setDesignVersion] = useLocalStorage<DesignVersion>(
    getDesignVersionStorageKey(group),
    defaultVersion,
  )
  const designVersion = isGated && !canToggle ? 'legacy' : storedVersion
  const { Navigate } = routerFactory.useRouter()
  const navigate = useNavigate()
  const location = useLocation()
  const { captureEvent } = useCaptureEvent()

  const toggle = () => {
    captureEvent('user_action', {
      from_path: location.pathname,
      name: 'design_toggle',
      screen,
      to_path: routerFactory.getPathString(to),
    })
    setDesignVersion(designVersion === 'new' ? 'legacy' : 'new')
    // replace (not push): a visit to this feature should occupy one history
    // slot regardless of how many times the user toggles mid-visit, so Back
    // always leaves the feature in a single press
    navigate(routerFactory.getPathString(to), { replace: true })
  }

  const redirect = designVersion !== version ? <Navigate replace to={to} /> : null

  return { canToggle, redirect, toggle }
}

/**
 * Read-only counterpart of `useDesignVersionToggle`, for links that navigate *into* a
 * toggle-backed feature from elsewhere (nav bar, dropdowns, cross-feature links) — picks
 * whichever of `paths.new` / `paths.legacy` matches the stored preference, instead of hardcoding
 * one variant. The page itself still owns persisting/switching the preference; this only reads
 * it, so the link lands on the right design without bouncing through the other route's redirect.
 */
export const useDesignVersionPath = <T,>(
  group: DesignVersionGroup,
  paths: Record<DesignVersion, T>,
): T => {
  const role = useSelector(selectUserRole)
  const { canToggle, defaultVersion, isGated } = getDesignVersionGate(group, role)
  const [storedVersion] = useLocalStorage<DesignVersion>(
    getDesignVersionStorageKey(group),
    defaultVersion,
  )
  return paths[isGated && !canToggle ? 'legacy' : storedVersion]
}

/**
 * Persists a design preference outside a mounted toggle (e.g. the Beta page, so a link into a
 * new design isn't bounced back to its legacy route). Same storage format as `useLocalStorage`.
 */
export const setStoredDesignVersion = (group: DesignVersionGroup, version: DesignVersion) => {
  try {
    localStorage.setItem(
      getDesignVersionStorageKey(group),
      JSON.stringify({ value: version, version: 'v1' }),
    )
  } catch {
    // Note: storage unavailable (private mode) - the link still opens, just may land on legacy.
  }
}
